import { awsPartitionFromUse } from "@p0security/integrations-aws-shared/gov-cloud";
import { rdsLambdaName } from "@p0security/integrations-aws-shared/policy/policies";
import { awsRoleName } from "@p0security/integrations-aws-shared/policy/shared";
import type { AwsIntegration } from "@p0security/integrations-aws-shared/types";
import type { PostgresLegacyAccessManagementConfig } from "@p0security/integrations-postgres-legacy-shared/types";
import type { Instructions } from "@p0security/shared/install/types";
import type { DevEnv } from "@p0security/shared/types/environment";
import { assertNever } from "@p0security/shared/types/index";
import { Typography } from "@p0security/ui";

import type { BuiltSqlCommands } from "./sqlBuilder";
import { buildSql } from "./sqlBuilder";

const PG_SERVICE_ROLE = "p0accessmanager";

const sanitizeSid = (sid: string) => {
  // Remove any characters that are not alphanumeric or underscore
  // This is to prevent any issues with IAM policy validation
  // see: https://docs.aws.amazon.com/IAM/latest/UserGuide/reference_policies_elements_sid.html
  return sid.replace(/[^a-zA-Z0-9_]/g, "");
};

const makeOutput = (commands: string, sql: BuiltSqlCommands) => {
  return {
    help: (
      <Typography.Paragraph>
        Follow the steps below to configure your RDS instance and Postgres
        database to allow P0 to manage access. The RDS instance must have IAM
        database authentication enabled. All of these commands only need to be
        run once per RDS instance; if you are configuring multiple databases on
        the same instance and have already run these commands on this instance,
        you can click &quot;Next&quot; to continue.
      </Typography.Paragraph>
    ),
    commands: {
      shell: [
        {
          header: (
            <>
              <Typography.Paragraph>
                <strong>Step 1: </strong>Run the following commands to authorize
                the P0 service account and provide the necessary permissions to
                establish a connection with the RDS instance.
              </Typography.Paragraph>
            </>
          ),
          command: commands,
        },
        // The next two items can probably be moved verbatim to a set of common instructions,
        // whenever we get another Postgres integration type (native, AWS RDS, etc.)
        {
          header: (
            <>
              <Typography.Paragraph>
                <strong>Step 2:</strong> Run the following SQL in your RDS
                instance to create the required functions.
              </Typography.Paragraph>
              <ul>
                <li>
                  <Typography.Paragraph>
                    Ensure you connect to the RDS instance as an authorized user
                    with these required permissions:
                  </Typography.Paragraph>
                </li>
                <ul>
                  <li>Permission to create (CREATE) users.</li>
                  <li>Permission to grant access (GRANT) to other users.</li>
                  <li>
                    Permission to create functions (CREATE FUNCTION) within the
                    database.
                  </li>
                </ul>
              </ul>
              <Typography.Paragraph>
                <strong>Step 2a:</strong> Create the p0CreateOrDeleteUser
                function:
              </Typography.Paragraph>
            </>
          ),
          // Will be defined for RDS install type
          command: sql.createOrDeleteUser ?? "",
        },
        {
          header: (
            <Typography.Paragraph>
              <strong>Step 2b:</strong> Create the p0GrantOrRevokeUserAccess
              function:
            </Typography.Paragraph>
          ),
          command: sql.grantOrRevokeAccess,
        },
        {
          header: (
            <Typography.Paragraph>
              <strong>Step 2c:</strong> Create the p0ManageRoleWithGrants
              function:
            </Typography.Paragraph>
          ),
          command: sql.manageRoleWithGrants,
        },
        {
          header: (
            <Typography.Paragraph>
              <strong>Step 3:</strong> Grant the P0 service account execute
              access to the newly created function:
            </Typography.Paragraph>
          ),
          command: sql.grantPermissions,
        },
      ],
    },
  };
};

export const awsRdsInstructor = (
  aws: AwsIntegration,
  context: DevEnv,
  item: Readonly<PostgresLegacyAccessManagementConfig>
): Instructions<any> => {
  if (item.installType.type !== "rds") {
    return {};
  }
  switch (item.installType.connectivity.type) {
    case "public":
      return awsRdsInstructorPublic(aws, context, item);
    case "private":
      return awsRdsInstructorPrivate(aws, item);
    default:
      return assertNever(item.installType.connectivity);
  }
};

const awsRdsInstructorPublic = (
  aws: AwsIntegration,
  context: DevEnv,
  item: Readonly<PostgresLegacyAccessManagementConfig>
): Instructions<any> => {
  // Return empty for CloudSQL.
  if (item.installType.type !== "rds") {
    return {};
  }

  if (item.installType.connectivity.type !== "public") {
    return {};
  }

  const { installType } = item;
  const { region, account, resourceId } = installType;
  const sql = buildSql(PG_SERVICE_ROLE, "rds");

  const roleName = awsRoleName(context, "iam-write");

  const awsPartition = awsPartitionFromUse(aws, account, "iam-write");

  const policy = {
    Version: "2012-10-17",
    Statement: [
      {
        Sid: `P0RdsManage${sanitizeSid(item.installType.resourceId)}`,
        Effect: "Allow",
        Action: "rds-db:connect",
        Resource: `arn:${awsPartition}:rds-db:${region}:${account}:dbuser:${resourceId}/p0accessmanager`,
      },
    ],
  };

  const commands = `
aws iam put-role-policy \\
  --role-name "${roleName}" \\
  --policy-name "P0RdsAccessManager" \\
  --policy-document '${JSON.stringify(policy, undefined, 2)}'
`.trim();

  return makeOutput(commands, sql);
};

const awsRdsInstructorPrivate = (
  aws: AwsIntegration,
  item: Readonly<PostgresLegacyAccessManagementConfig>
): Instructions<any> => {
  if (item.installType.type !== "rds") {
    return {};
  }

  if (item.installType.connectivity.type !== "private") {
    return {};
  }

  const { installType } = item;
  const { region, account, resourceId } = installType;
  const securityPerimeterId = item.installType.connectivity.securityPerimeter;
  const securityGroupId = item.installType.connectivity.securityGroupId;

  const awsPartition = awsPartitionFromUse(aws, account, "iam-write");

  const securityPerimeter =
    aws?.["rds-security-perimeter"]?.[securityPerimeterId];
  const { vpcId } = securityPerimeter;

  const sql = buildSql(PG_SERVICE_ROLE, "rds");

  const functionName = rdsLambdaName(vpcId);
  const lambdaRoleName = `P0RDSPerimeterExecutionRole-${vpcId}`;

  const sid = sanitizeSid(item.installType.resourceId);

  const policy = {
    Version: "2012-10-17",
    Statement: [
      {
        Sid: `P0RdsManager${sid}`,
        Effect: "Allow",
        Action: "rds-db:connect",
        Resource: `arn:${awsPartition}:rds-db:${region}:${account}:dbuser:${resourceId}/${PG_SERVICE_ROLE}`,
      },
    ],
  };

  const commands = `
LAMBDA_SG=$(aws lambda get-function \\
  --function-name ${functionName} \\
  --query "Configuration.VpcConfig.SecurityGroupIds" \\
  --output text) && \\
aws ec2 authorize-security-group-ingress \\
  --group-id ${securityGroupId} \\
  --protocol tcp \\
  --port ${installType.port ?? 5432} \\
  --source-group $LAMBDA_SG \\

aws iam put-role-policy \\
  --role-name "${lambdaRoleName}" \\
  --policy-name "P0RdsAccessManager${sid}" \\
  --policy-document '${JSON.stringify(policy, undefined, 2)}'
`.trim();

  return makeOutput(commands, sql);
};
